import { useEffect, useMemo, useState } from 'react'
import { Link, Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { FiArrowLeft, FiCheck, FiCreditCard, FiFileText, FiLock } from 'react-icons/fi'
import toast from 'react-hot-toast'
import { getAuth } from 'firebase/auth'
import { useAuth } from '../contexts/AuthContext'
import { useUserRealtimeData } from '../hooks/useUserRealtimeData'
import { getCheckoutPlan } from '../config/plans'
import Card from '../components/ui/Card'
import Button from '../components/ui/Button'
import DeviceRequirementModal from '../components/DeviceRequirementModal'
import AuthModal from '../components/AuthModal'
import PixPaymentPanel from '../components/PixPaymentPanel'
import { hideProducts } from '../config/featureFlags'
import {
  ANNUAL_ACCESS_MONTHS,
  ANNUAL_CHECKOUT_TERMS,
  ANNUAL_MARKETING_TAGLINE,
  ANNUAL_MAX_INSTALLMENTS,
  ANNUAL_PAID_MONTHS,
  ANNUAL_PIX_CHECKOUT_TERMS,
  EXPIRY_NOTICE_DAYS,
  formatPlanCurrency,
  getAnnualTotal,
  getInstallmentValue,
  getMonthlyAmount,
  isPaidPlan,
  RENEWAL_NOTICE_DAYS,
} from '../utils/subscriptionPlan'
import { apiUrl } from '../config/api'
import LegalConsentText from '../components/LegalConsentText'

const FIELD = 'input-field !min-h-[42px] !py-2.5 !px-4 !text-sm'

const CheckoutPage = () => {
  const { planId } = useParams()
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const { user, loading: authLoading } = useAuth()
  const { profile, loading: userDataLoading } = useUserRealtimeData()

  const plan = getCheckoutPlan(planId)
  const billingCycle = searchParams.get('cycle') === 'annual' ? 'annual' : 'monthly'
  const isAnnual = billingCycle === 'annual'

  const [showAuthModal, setShowAuthModal] = useState(false)
  const [loading, setLoading] = useState(false)
  const [cepLoading, setCepLoading] = useState(false)
  const [acceptedLegal, setAcceptedLegal] = useState(false)
  const [acceptedTerms, setAcceptedTerms] = useState(false)
  const [paymentMethod, setPaymentMethod] = useState('card')
  const [installmentCount, setInstallmentCount] = useState(1)
  const [pixCheckout, setPixCheckout] = useState(null)

  const [paymentData, setPaymentData] = useState({
    cardNumber: '',
    cardName: '',
    expiryDate: '',
    cvv: '',
    email: '',
    cpf: '',
  })

  const [addressData, setAddressData] = useState({
    name: '',
    phone: '',
    postalCode: '',
    address: '',
    addressNumber: '',
    complement: '',
    province: '',
    city: '',
    state: '',
  })

  const hasDevice = Boolean(profile?.deviceOwned) || Boolean(profile?.deviceClaimed)

  const monthlyAmount = useMemo(() => getMonthlyAmount(planId), [planId])
  const annualTotal = useMemo(() => getAnnualTotal(planId), [planId])
  const chargeAmount = isAnnual ? annualTotal : monthlyAmount

  useEffect(() => {
    if (user?.email && !paymentData.email) {
      setPaymentData((prev) => ({ ...prev, email: user.email }))
    }
  }, [user?.email, paymentData.email])

  useEffect(() => {
    if (isAnnual && paymentMethod === 'pix') {
      setInstallmentCount(1)
    }
  }, [isAnnual, paymentMethod])

  const setBillingCycle = (cycle) => {
    setSearchParams(cycle === 'annual' ? { cycle: 'annual' } : {}, { replace: true })
    if (cycle === 'monthly') setAcceptedTerms(false)
  }

  if (!plan || !isPaidPlan(planId)) {
    return <Navigate to="/#pricing" replace />
  }

  if (authLoading) return null

  if (!user) {
    return (
      <>
        <main className="w-full min-w-0 pt-24 sm:pt-28 pb-16">
          <div className="container-custom max-w-lg text-center">
            <h1 className="text-2xl font-extrabold text-gray-900 dark:text-white mb-3">Faça login para continuar</h1>
            <p className="text-gray-600 dark:text-gray-300 mb-6">
              Você precisa estar logado para assinar o plano {plan.name}.
            </p>
            <Button variant="primary" size="lg" onClick={() => setShowAuthModal(true)}>
              Entrar ou criar conta
            </Button>
            <Link to="/#pricing" className="block mt-4 text-sm text-accent font-semibold hover:underline">
              Voltar aos planos
            </Link>
          </div>
        </main>
        {showAuthModal && <AuthModal onClose={() => setShowAuthModal(false)} />}
      </>
    )
  }

  if (!userDataLoading && !hasDevice) {
    return (
      <DeviceRequirementModal
        onClose={() => navigate('/#pricing')}
        onConfirmed={() => {}}
        onNoDevice={() => {
          if (!hideProducts) navigate('/#products')
          else navigate('/#contact')
        }}
      />
    )
  }

  const handleCardNumberChange = (e) => {
    let value = e.target.value.replace(/\D/g, '')
    value = value.replace(/(\d{4})(?=\d)/g, '$1 ')
    setPaymentData({ ...paymentData, cardNumber: value })
  }

  const handleExpiryChange = (e) => {
    let value = e.target.value.replace(/\D/g, '')
    value = value.replace(/(\d{2})(?=\d)/g, '$1/')
    setPaymentData({ ...paymentData, expiryDate: value })
  }

  const validateForm = () => {
    const cepNumber = addressData.postalCode.replace(/\D/g, '')
    if (!addressData.name || !addressData.phone) {
      toast.error('Preencha nome e telefone.')
      return false
    }
    if (cepNumber.length !== 8) {
      toast.error('CEP inválido. Informe um CEP com 8 dígitos.')
      return false
    }
    if (!addressData.address || !addressData.city || !addressData.state || !addressData.province) {
      toast.error('Preencha todos os campos de endereço.')
      return false
    }
    const cpfNumber = paymentData.cpf.replace(/\D/g, '')
    if (cpfNumber.length < 11) {
      toast.error('CPF inválido.')
      return false
    }
    if (!paymentData.email) {
      toast.error('Informe o e-mail.')
      return false
    }
    if (paymentMethod === 'card') {
      const cardNumberDigits = paymentData.cardNumber.replace(/\D/g, '')
      if (cardNumberDigits.length < 13) {
        toast.error('Número do cartão inválido.')
        return false
      }
      if (!/^\d{2}\/\d{2}$/.test(paymentData.expiryDate)) {
        toast.error('Validade inválida. Use MM/AA.')
        return false
      }
      if (paymentData.cvv.replace(/\D/g, '').length < 3) {
        toast.error('CVV inválido.')
        return false
      }
    }
    if (!acceptedLegal) {
      toast.error('Aceite a Política de Privacidade e os Termos de Uso para continuar.')
      return false
    }
    if (isAnnual && !acceptedTerms) {
      toast.error('Aceite os termos do plano anual para continuar.')
      return false
    }
    return true
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!validateForm()) return

    setLoading(true)
    try {
      toast.loading('Processando pagamento...', { id: 'payment' })
      const idToken = await getAuth().currentUser?.getIdToken()
      if (!idToken) throw new Error('Sessão expirada. Faça login novamente.')

      const response = await fetch(apiUrl('/api/asaas/checkout'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          planId: plan.id,
          planName: plan.name,
          paymentMethod,
          billingCycle,
          autoRenew: isAnnual && paymentMethod === 'card',
          acceptedTerms: isAnnual ? acceptedTerms : acceptedLegal,
          acceptedPrivacy: acceptedLegal,
          addressData: {
            ...addressData,
            email: paymentData.email,
            cpfCnpj: paymentData.cpf.replace(/\D/g, ''),
          },
          cardData:
            paymentMethod === 'card'
              ? {
                  cardNumber: paymentData.cardNumber,
                  cardName: paymentData.cardName,
                  expiryDate: paymentData.expiryDate,
                  cvv: paymentData.cvv,
                }
              : null,
          installmentCount: isAnnual && paymentMethod === 'card' ? installmentCount : 1,
        }),
      })

      const raw = await response.text()
      const result = (() => {
        try {
          return JSON.parse(raw)
        } catch {
          return null
        }
      })()
      if (!result) {
        throw new Error(`Resposta inválida do backend. Início: ${raw.slice(0, 60)}`)
      }
      if (!response.ok || !result.ok) {
        throw new Error(result?.error || 'Erro ao processar pagamento.')
      }

      if (result.pix) {
        setPixCheckout({ pix: result.pix, amount: chargeAmount })
        toast.success(
          result.pendingPayment
            ? 'PIX gerado! Seu plano será ativado após a confirmação do pagamento.'
            : 'PIX gerado! Escaneie o QR Code ou copie o código.',
          { id: 'payment' }
        )
        window.scrollTo(0, 0)
        return
      }

      toast.success(
        isAnnual ? 'Plano anual contratado com sucesso!' : 'Assinatura mensal criada com sucesso!',
        { id: 'payment' }
      )
      navigate('/account')
    } catch (error) {
      console.error(error)
      toast.error(error.message || 'Erro ao processar pagamento.', { id: 'payment' })
    } finally {
      setLoading(false)
    }
  }

  const PlanIcon = plan.icon

  return (
    <main className="w-full min-w-0 pt-24 sm:pt-28 pb-16">
      <div className="container-custom">
        <Link
          to="/#pricing"
          className="inline-flex items-center gap-2 text-sm font-semibold text-gray-600 dark:text-gray-400 hover:text-accent mb-6"
        >
          <FiArrowLeft className="w-4 h-4" />
          Voltar aos planos
        </Link>

        <div className="mb-8">
          <p className="text-xs font-bold text-accent uppercase tracking-[0.2em] mb-2">Checkout</p>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-gray-900 dark:text-white tracking-tight">
            Assinar plano {plan.name}
          </h1>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
          {pixCheckout ? (
            <div className="lg:col-span-2 max-w-xl">
              <PixPaymentPanel
                pix={pixCheckout.pix}
                amountLabel={formatPlanCurrency(pixCheckout.amount)}
                onDone={() => navigate('/account')}
              />
            </div>
          ) : (
          <div className="lg:col-span-2 space-y-6">
            <Card padding="lg" hover={false}>
              <h2 className="text-lg font-extrabold text-gray-900 dark:text-white mb-4">Periodicidade</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setBillingCycle('monthly')}
                  className={`p-4 rounded-xl border-2 text-left transition-all ${
                    billingCycle === 'monthly'
                      ? 'border-accent bg-accent/5 dark:bg-accent/10'
                      : 'border-gray-200 dark:border-gray-700 hover:border-accent/50'
                  }`}
                >
                  <p className="font-bold text-gray-900 dark:text-white">Mensal</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    {formatPlanCurrency(monthlyAmount)}/mês · cobrança recorrente
                  </p>
                </button>
                <button
                  type="button"
                  onClick={() => setBillingCycle('annual')}
                  className={`relative p-4 rounded-xl border-2 text-left transition-all ${
                    billingCycle === 'annual'
                      ? 'border-accent bg-accent/5 dark:bg-accent/10'
                      : 'border-gray-200 dark:border-gray-700 hover:border-accent/50'
                  }`}
                >
                  <span className="absolute -top-2 right-3 bg-emerald-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full uppercase">
                    {ANNUAL_MARKETING_TAGLINE}
                  </span>
                  <p className="font-bold text-gray-900 dark:text-white">Anual</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    {formatPlanCurrency(annualTotal)}/ano · {ANNUAL_PAID_MONTHS} meses pagos, {ANNUAL_ACCESS_MONTHS} de acesso
                  </p>
                </button>
              </div>
              {isAnnual && (
                <p className="mt-3 text-sm text-gray-600 dark:text-gray-400">
                  {paymentMethod === 'card'
                    ? `Renovação automática no cartão de crédito, com aviso por e-mail ${RENEWAL_NOTICE_DAYS} dias antes.`
                    : 'Pagamento via PIX: 12 meses de acesso, sem renovação automática. Você receberá avisos por e-mail 5 e 3 dias antes do encerramento.'}
                </p>
              )}
            </Card>

            <form onSubmit={handleSubmit} className="space-y-6">
              <Card padding="lg" hover={false}>
                <h2 className="text-lg font-extrabold text-gray-900 dark:text-white mb-4">Seus dados</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="sm:col-span-2">
                    <label className="form-label">Nome completo</label>
                    <input
                      type="text"
                      required
                      value={addressData.name}
                      onChange={(e) => setAddressData({ ...addressData, name: e.target.value })}
                      className={FIELD}
                    />
                  </div>
                  <div>
                    <label className="form-label">Telefone</label>
                    <input
                      type="tel"
                      required
                      value={addressData.phone}
                      onChange={(e) => setAddressData({ ...addressData, phone: e.target.value })}
                      className={FIELD}
                    />
                  </div>
                  <div>
                    <label className="form-label">E-mail</label>
                    <input
                      type="email"
                      required
                      value={paymentData.email}
                      onChange={(e) => setPaymentData({ ...paymentData, email: e.target.value })}
                      className={FIELD}
                    />
                  </div>
                  <div>
                    <label className="form-label">CPF</label>
                    <input
                      type="text"
                      required
                      value={paymentData.cpf}
                      onChange={(e) => {
                        let value = e.target.value.replace(/\D/g, '')
                        value = value.replace(/(\d{3})(\d)/, '$1.$2')
                        value = value.replace(/(\d{3})(\d)/, '$1.$2')
                        value = value.replace(/(\d{3})(\d{1,2})$/, '$1-$2')
                        setPaymentData({ ...paymentData, cpf: value })
                      }}
                      maxLength={14}
                      className={FIELD}
                    />
                  </div>
                  <div>
                    <label className="form-label">CEP</label>
                    <input
                      type="text"
                      required
                      value={addressData.postalCode}
                      onChange={(e) => setAddressData({ ...addressData, postalCode: e.target.value })}
                      onBlur={async () => {
                        const cep = addressData.postalCode.replace(/\D/g, '')
                        if (cep.length !== 8) return
                        try {
                          setCepLoading(true)
                          const resp = await fetch(`https://viacep.com.br/ws/${cep}/json/`)
                          const data = await resp.json()
                          if (data.erro) {
                            toast.error('CEP não encontrado.')
                            return
                          }
                          setAddressData((prev) => ({
                            ...prev,
                            address: data.logradouro || prev.address,
                            province: data.bairro || prev.province,
                            city: data.localidade || prev.city,
                            state: data.uf || prev.state,
                          }))
                        } catch {
                          toast.error('Erro ao buscar CEP.')
                        } finally {
                          setCepLoading(false)
                        }
                      }}
                      className={FIELD}
                    />
                    {cepLoading && (
                      <p className="mt-1 text-xs text-gray-500">Buscando endereço...</p>
                    )}
                  </div>
                  <div className="sm:col-span-2">
                    <label className="form-label">Endereço</label>
                    <input
                      type="text"
                      required
                      value={addressData.address}
                      onChange={(e) => setAddressData({ ...addressData, address: e.target.value })}
                      className={FIELD}
                    />
                  </div>
                  <div>
                    <label className="form-label">Número</label>
                    <input
                      type="text"
                      required
                      value={addressData.addressNumber}
                      onChange={(e) => setAddressData({ ...addressData, addressNumber: e.target.value })}
                      className={FIELD}
                    />
                  </div>
                  <div>
                    <label className="form-label">Bairro</label>
                    <input
                      type="text"
                      required
                      value={addressData.province}
                      onChange={(e) => setAddressData({ ...addressData, province: e.target.value })}
                      className={FIELD}
                    />
                  </div>
                  <div>
                    <label className="form-label">Cidade</label>
                    <input
                      type="text"
                      required
                      value={addressData.city}
                      onChange={(e) => setAddressData({ ...addressData, city: e.target.value })}
                      className={FIELD}
                    />
                  </div>
                  <div>
                    <label className="form-label">UF</label>
                    <input
                      type="text"
                      required
                      maxLength={2}
                      value={addressData.state}
                      onChange={(e) => setAddressData({ ...addressData, state: e.target.value.toUpperCase() })}
                      className={FIELD}
                    />
                  </div>
                </div>
              </Card>

              <Card padding="lg" hover={false}>
                <h2 className="text-lg font-extrabold text-gray-900 dark:text-white mb-4">Pagamento</h2>

                {!isAnnual ? (
                  <div className="grid grid-cols-3 gap-3 mb-5">
                    <button
                      type="button"
                      onClick={() => setPaymentMethod('card')}
                      className={`flex items-center justify-center gap-2 p-3 rounded-xl border-2 font-semibold text-sm min-h-[48px] ${
                        paymentMethod === 'card'
                          ? 'border-accent bg-accent text-white'
                          : 'border-gray-300 dark:border-gray-700'
                      }`}
                    >
                      <FiCreditCard className="w-5 h-5" />
                      Cartão
                    </button>
                    <button
                      type="button"
                      onClick={() => setPaymentMethod('pix')}
                      className={`flex items-center justify-center gap-2 p-3 rounded-xl border-2 font-semibold text-sm min-h-[48px] ${
                        paymentMethod === 'pix'
                          ? 'border-accent bg-accent text-white'
                          : 'border-gray-300 dark:border-gray-700'
                      }`}
                    >
                      <span className="font-extrabold tracking-tight">PIX</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setPaymentMethod('boleto')}
                      className={`flex items-center justify-center gap-2 p-3 rounded-xl border-2 font-semibold text-sm min-h-[48px] ${
                        paymentMethod === 'boleto'
                          ? 'border-accent bg-accent text-white'
                          : 'border-gray-300 dark:border-gray-700'
                      }`}
                    >
                      <FiFileText className="w-5 h-5" />
                      Boleto
                    </button>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-3 mb-5">
                    <button
                      type="button"
                      onClick={() => setPaymentMethod('card')}
                      className={`flex items-center justify-center gap-2 p-3 rounded-xl border-2 font-semibold text-sm min-h-[48px] ${
                        paymentMethod === 'card'
                          ? 'border-accent bg-accent text-white'
                          : 'border-gray-300 dark:border-gray-700'
                      }`}
                    >
                      <FiCreditCard className="w-5 h-5" />
                      Cartão
                    </button>
                    <button
                      type="button"
                      onClick={() => setPaymentMethod('pix')}
                      className={`flex items-center justify-center gap-2 p-3 rounded-xl border-2 font-semibold text-sm min-h-[48px] ${
                        paymentMethod === 'pix'
                          ? 'border-accent bg-accent text-white'
                          : 'border-gray-300 dark:border-gray-700'
                      }`}
                    >
                      <span className="font-extrabold tracking-tight">PIX</span>
                    </button>
                  </div>
                )}

                {isAnnual && paymentMethod === 'card' && (
                  <p className="mb-4 text-sm text-gray-600 dark:text-gray-400 rounded-xl bg-gray-50 dark:bg-gray-800/80 border border-gray-200 dark:border-gray-700 p-3">
                    Cartão: renovação automática anual com aviso prévio por e-mail.
                  </p>
                )}

                {isAnnual && paymentMethod === 'pix' && (
                  <p className="mb-4 text-sm text-gray-600 dark:text-gray-400 rounded-xl bg-amber-50/80 dark:bg-amber-500/10 border border-amber-300/50 dark:border-amber-500/30 p-3">
                    PIX: pagamento único com <strong>12 meses de acesso</strong>. Ao encerrar, você volta ao plano Gratuito e precisa renovar manualmente.
                  </p>
                )}

                {paymentMethod === 'card' ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {isAnnual && (
                      <div className="sm:col-span-2">
                        <label className="form-label">Parcelamento no cartão</label>
                        <select
                          value={installmentCount}
                          onChange={(e) => setInstallmentCount(Number(e.target.value))}
                          className={FIELD}
                        >
                          {Array.from({ length: ANNUAL_MAX_INSTALLMENTS }, (_, i) => i + 1).map((n) => (
                            <option key={n} value={n}>
                              {n === 1
                                ? `À vista — ${formatPlanCurrency(annualTotal)}`
                                : `${n}x de ${formatPlanCurrency(getInstallmentValue(annualTotal, n))} sem juros`}
                            </option>
                          ))}
                        </select>
                        {installmentCount > 1 && (
                          <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                            Total de {formatPlanCurrency(annualTotal)} em {installmentCount} parcelas. A renovação
                            anual seguinte será cobrada em 1x no cartão cadastrado.
                          </p>
                        )}
                      </div>
                    )}
                    <div className="sm:col-span-2">
                      <label className="form-label">Número do cartão</label>
                      <input
                        type="text"
                        required
                        value={paymentData.cardNumber}
                        onChange={handleCardNumberChange}
                        maxLength={19}
                        className={FIELD}
                        placeholder="0000 0000 0000 0000"
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <label className="form-label">Nome no cartão</label>
                      <input
                        type="text"
                        required
                        value={paymentData.cardName}
                        onChange={(e) => setPaymentData({ ...paymentData, cardName: e.target.value.toUpperCase() })}
                        className={FIELD}
                      />
                    </div>
                    <div>
                      <label className="form-label">Validade</label>
                      <input
                        type="text"
                        required
                        value={paymentData.expiryDate}
                        onChange={handleExpiryChange}
                        maxLength={5}
                        className={FIELD}
                        placeholder="MM/AA"
                      />
                    </div>
                    <div>
                      <label className="form-label">CVV</label>
                      <input
                        type="text"
                        required
                        value={paymentData.cvv}
                        onChange={(e) => {
                          const value = e.target.value.replace(/\D/g, '').slice(0, 4)
                          setPaymentData({ ...paymentData, cvv: value })
                        }}
                        maxLength={4}
                        className={FIELD}
                      />
                    </div>
                  </div>
                ) : paymentMethod === 'pix' ? (
                  <p className="text-sm text-gray-600 dark:text-gray-400 rounded-xl bg-accent/10 dark:bg-accent/20 border border-accent/30 p-4">
                    {isAnnual
                      ? 'Ao confirmar, geraremos o QR Code PIX para o plano anual. Seu acesso será de 12 meses após a confirmação do pagamento.'
                      : 'Ao confirmar, geraremos o QR Code PIX para pagamento imediato. As próximas mensalidades também serão enviadas via PIX.'}
                  </p>
                ) : (
                  <p className="text-sm text-gray-600 dark:text-gray-400 rounded-xl bg-accent/10 dark:bg-accent/20 border border-accent/30 p-4">
                    O boleto será gerado e enviado para seu e-mail. A confirmação pode levar até 2 dias úteis.
                  </p>
                )}
              </Card>

              <Card padding="lg" hover={false}>
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={acceptedLegal}
                    onChange={(e) => setAcceptedLegal(e.target.checked)}
                    className="mt-1 h-4 w-4 rounded border-gray-300 text-accent"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
                    <LegalConsentText /> Autorizo o tratamento dos meus dados pessoais e de pagamento
                    (CPF, endereço e cobrança) para contratação do plano, incluindo compartilhamento com a
                    central de pagamentos.
                  </span>
                </label>
              </Card>

              {isAnnual && (
                <Card padding="lg" hover={false} className="border-amber-300/50 dark:border-amber-500/30">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={acceptedTerms}
                      onChange={(e) => setAcceptedTerms(e.target.checked)}
                      className="mt-1 h-4 w-4 rounded border-gray-300 text-accent"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
                      {paymentMethod === 'pix' ? ANNUAL_PIX_CHECKOUT_TERMS : ANNUAL_CHECKOUT_TERMS}
                    </span>
                  </label>
                </Card>
              )}

              <div className="flex items-center gap-3 text-sm text-gray-600 dark:text-gray-400">
                <FiLock className="w-5 h-5 text-accent flex-shrink-0" />
                Pagamento seguro e criptografado
              </div>

              <Button type="submit" variant="primary" size="lg" className="w-full sm:w-auto" disabled={loading}>
                {loading
                  ? 'Processando...'
                  : isAnnual
                    ? paymentMethod === 'pix'
                      ? `Gerar PIX anual — ${formatPlanCurrency(chargeAmount)}`
                      : installmentCount > 1
                        ? `Contratar em ${installmentCount}x — ${formatPlanCurrency(getInstallmentValue(annualTotal, installmentCount))}/parcela`
                        : `Contratar plano anual — ${formatPlanCurrency(chargeAmount)}`
                    : paymentMethod === 'card'
                      ? 'Pagar com cartão'
                      : paymentMethod === 'pix'
                        ? 'Gerar PIX'
                        : 'Gerar boleto'}
              </Button>
            </form>
          </div>
          )}

          {/* Resumo */}
          <div className="lg:col-span-1">
            <div className="lg:sticky lg:top-28">
              <Card padding="lg" hover={false} glow={plan.highlight}>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-xl bg-accent/10 dark:bg-accent/20 flex items-center justify-center text-accent">
                    <PlanIcon className="w-6 h-6" />
                  </div>
                  <div>
                    <p className="text-xs font-bold text-accent uppercase tracking-wider">Resumo</p>
                    <h2 className="text-xl font-extrabold text-gray-900 dark:text-white">{plan.name}</h2>
                  </div>
                </div>

                <div className="space-y-3 pb-4 mb-4 border-b border-gray-200 dark:border-gray-700 text-sm">
                  <div className="flex justify-between gap-4">
                    <span className="text-gray-600 dark:text-gray-400">Referência mensal</span>
                    <span className="font-semibold text-gray-900 dark:text-white">
                      {formatPlanCurrency(monthlyAmount)}/mês
                    </span>
                  </div>
                  {isAnnual ? (
                    <>
                      <div className="flex justify-between gap-4">
                        <span className="text-gray-600 dark:text-gray-400">Cobrança anual</span>
                        <span className="font-bold text-accent">{formatPlanCurrency(annualTotal)}</span>
                      </div>
                      {installmentCount > 1 && (
                        <div className="flex justify-between gap-4">
                          <span className="text-gray-600 dark:text-gray-400">Parcelamento</span>
                          <span className="font-semibold">
                            {installmentCount}x de {formatPlanCurrency(getInstallmentValue(annualTotal, installmentCount))}
                          </span>
                        </div>
                      )}
                      <div className="flex justify-between gap-4">
                        <span className="text-gray-600 dark:text-gray-400">Equivalente a</span>
                        <span className="font-semibold">{ANNUAL_PAID_MONTHS} meses</span>
                      </div>
                      <div className="flex justify-between gap-4">
                        <span className="text-gray-600 dark:text-gray-400">Acesso por</span>
                        <span className="font-semibold">{ANNUAL_ACCESS_MONTHS} meses</span>
                      </div>
                    </>
                  ) : (
                    <div className="flex justify-between gap-4">
                      <span className="text-gray-600 dark:text-gray-400">Cobrança</span>
                      <span className="font-bold text-accent">{formatPlanCurrency(monthlyAmount)}/mês</span>
                    </div>
                  )}
                </div>

                <ul className="space-y-2 mb-4">
                  {plan.features.slice(0, 5).map((feature) => (
                    <li key={feature} className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300">
                      <FiCheck className="w-4 h-4 text-accent flex-shrink-0 mt-0.5" />
                      {feature}
                    </li>
                  ))}
                </ul>

                {isAnnual && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
                    {paymentMethod === 'pix'
                      ? `${ANNUAL_MARKETING_TAGLINE} Sem renovação automática — avisos por e-mail ${EXPIRY_NOTICE_DAYS.join(' e ')} dias antes do encerramento.`
                      : `${ANNUAL_MARKETING_TAGLINE} Renovação automática no cartão, com aviso prévio por e-mail.`}
                  </p>
                )}
              </Card>
            </div>
          </div>
        </div>
      </div>
    </main>
  )
}

export default CheckoutPage
